'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useOMS } from '../../../apps/hooks/oms/useOMS';
import type { OrderFulfillmentStatusListResponse } from '../../../infrastructure/OMSClient';
import { FulfillmentSummaryCards } from './components/FulfillmentSummaryCards';
import { FulfillmentStatusTable } from './components/FulfillmentStatusTable';

type ViewMode = 'sla' | 'pic';

export default function FulfillmentStatusPage() {
  const [data, setData] = useState<OrderFulfillmentStatusListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('sla');
  const oms = useOMS();

  const fetchFulfillmentStatus = async () => {
    try {
      setLoading(true);
      const response = await oms.getFulfillmentStatus();
      setData(response);
    } catch (error) {
      console.error('Error fetching fulfillment status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFulfillmentStatus();
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => {
        fetchFulfillmentStatus();
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full max-w-[95%] mx-auto py-4 space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold pb-3">Order Fulfillment Dashboard</h1>
        <div className="flex gap-2">
          <Button variant={viewMode === 'sla' ? 'default' : 'outline'} onClick={() => setViewMode('sla')}>
            Sort by SLA Risk
          </Button>
          <Button variant={viewMode === 'pic' ? 'default' : 'outline'} onClick={() => setViewMode('pic')}>
            Sort by PIC
          </Button>
          <Button variant="outline" onClick={fetchFulfillmentStatus} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {data && <FulfillmentSummaryCards summary={data.summary} total={data.total} />}

      <Card>
        <CardContent className="pt-6">
          {data && <FulfillmentStatusTable items={data.items} viewMode={viewMode} isLoading={loading} />}
          {!data && loading && <div className="text-center py-8">Loading fulfillment status...</div>}
        </CardContent>
      </Card>
    </div>
  );
}
